import React, { useState } from 'react';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  IconButton,
  Box,
  Grid,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import ApiServices from '../../../Utils/ApiServices/ApiServices';
import { getDecryptedAccessToken } from '../../../Utils/LoginUserDetails.js/LoginUserSavedDetails';

const BenefitsCreate = () => {
  const accessToken = getDecryptedAccessToken();

  const [formData, setFormData] = useState({
    benefit_name: '',
    subtitle: '',
    is_for_appointment: false,
    active: false,
    base64_image: '',
    description: [''],
    faqs: [{ question: '', answer: '' }],
    scopes: '',
    age_pricing: [{ min_age: '', max_age: '', price: '' }],

  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDescriptionChange = (index, value) => {
    const updated = [...formData.description];
    updated[index] = value;
    setFormData({ ...formData, description: updated });
  };

  const handleFAQChange = (index, key, value) => {
    const updated = [...formData.faqs];
    updated[index][key] = value;
    setFormData({ ...formData, faqs: updated });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, base64_image: reader.result }));
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const addDescription = () => {
    setFormData({ ...formData, description: [...formData.description, ''] });
  };

  const removeDescription = (index) => {
    const updated = [...formData.description];
    updated.splice(index, 1);
    setFormData({ ...formData, description: updated });
  };

  const addFAQ = () => {
    setFormData({ ...formData, faqs: [...formData.faqs, { question: '', answer: '' }] });
  };

  const removeFAQ = (index) => {
    const updated = [...formData.faqs];
    updated.splice(index, 1);
    setFormData({ ...formData, faqs: updated });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await ApiServices.post(
        `/apis/default/api/membershipbenefits`,
        formData,
        {
          Authorization: `Bearer ${accessToken}`,
        }
      );

      setSnackbar({ open: true, message: 'Benefit created successfully!', severity: 'success' });

      // Reset form
      setFormData({
        benefit_name: '',
        subtitle: '',
        is_for_appointment: false,
        active: false,
        base64_image: '',
        description: [''],
        faqs: [{ question: '', answer: '' }],
        scopes: '',
        age_pricing: [{ min_age: '', max_age: '', price: '' }],

      });
    } catch (error) {
      console.error('Error creating benefit:', error);
      setSnackbar({ open: true, message: 'Failed to create benefit.', severity: 'error' });
    }
    setLoading(false);
  };

  const handleAgePricingChange = (index, field, value) => {
  const updated = [...formData.age_pricing];
  updated[index][field] = value;
  setFormData({ ...formData, age_pricing: updated });
};

const addAgePricing = () => {
  setFormData({ ...formData, age_pricing: [...formData.age_pricing, { min_age: '', max_age: '', price: '' }] });
};

const removeAgePricing = (index) => {
  const updated = [...formData.age_pricing];
  updated.splice(index, 1);
  setFormData({ ...formData, age_pricing: updated });
};


  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>Create New Benefit</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Benefit Name"
            fullWidth
            value={formData.benefit_name}
            onChange={(e) => handleChange('benefit_name', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Subtitle"
            fullWidth
            value={formData.subtitle}
            onChange={(e) => handleChange('subtitle', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.is_for_appointment}
                onChange={(e) => handleChange('is_for_appointment', e.target.checked)}
              />
            }
            label="Is For Appointment"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
            }
            label="Active"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Scopes"
            fullWidth
            value={formData.scopes}
            onChange={(e) => handleChange('scopes', e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Button variant="outlined" component="label">
            Upload Image
            <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
          </Button>
          {formData.base64_image && (
            <Box mt={2}>
              <img
                src={formData.base64_image}
                alt="Preview"
                style={{ maxWidth: '200px', borderRadius: 8 }}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1">Descriptions</Typography>
      {formData.description.map((desc, idx) => (
        <Box key={idx} display="flex" alignItems="center" mb={1}>
          <TextField
            label={`Description ${idx + 1}`}
            fullWidth
            value={desc}
            onChange={(e) => handleDescriptionChange(idx, e.target.value)}
          />
          <IconButton onClick={() => removeDescription(idx)}>
            <Delete />
          </IconButton>
        </Box>
      ))}
      <Button startIcon={<Add />} onClick={addDescription}>
        Add Description
      </Button>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1">FAQs</Typography>
      {formData.faqs.map((faq, idx) => (
        <Grid container spacing={1} alignItems="center" key={idx} mb={1}>
          <Grid item xs={5}>
            <TextField
              label="Question"
              fullWidth
              value={faq.question}
              onChange={(e) => handleFAQChange(idx, 'question', e.target.value)}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              label="Answer"
              fullWidth
              value={faq.answer}
              onChange={(e) => handleFAQChange(idx, 'answer', e.target.value)}
            />
          </Grid>
          <Grid item xs={2}>
            <IconButton onClick={() => removeFAQ(idx)}>
              <Delete />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button startIcon={<Add />} onClick={addFAQ}>
        Add FAQ
      </Button>
      <Divider sx={{ my: 2 }} />
<Typography variant="subtitle1">Age Pricing</Typography>
{formData.age_pricing.map((pricing, idx) => (
  <Grid container spacing={1} alignItems="center" key={idx} mb={1}>
    <Grid item xs={3}>
      <TextField
        label="Min Age"
        type="number"
        fullWidth
        value={pricing.min_age}
        onChange={(e) => handleAgePricingChange(idx, 'min_age', e.target.value)}
      />
    </Grid>
    <Grid item xs={3}>
      <TextField
        label="Max Age"
        type="number"
        fullWidth
        value={pricing.max_age}
        onChange={(e) => handleAgePricingChange(idx, 'max_age', e.target.value)}
        placeholder="Leave blank for no max"
      />
    </Grid>
    <Grid item xs={4}>
      <TextField
        label="Price"
        type="number"
        fullWidth
        value={pricing.price}
        onChange={(e) => handleAgePricingChange(idx, 'price', e.target.value)}
      />
    </Grid>
    <Grid item xs={2}>
      <IconButton onClick={() => removeAgePricing(idx)}>
        <Delete />
      </IconButton>
    </Grid>
  </Grid>
))}
<Button startIcon={<Add />} onClick={addAgePricing}>
  Add Age Pricing
</Button>


      <Box mt={4}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Submitting...' : 'Create Benefit'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BenefitsCreate;
